import { useEffect } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import { useFullscreen } from '@iconik-io/shared-lib/src/react/hooks/use_fullscreen';

import {
  AI_METADATA_REVIEW_MODAL,
  FACE_CONFIRMATION_MODAL,
} from '../../constants/modals';
import { findAiMetadataGenerationForAsset } from '../../ducks/ai_metadata/selectors';
import { closeModal, openModal } from '../../ducks/modals_manager/slice';
import { getInConfirmationMode } from '../../ducks/panels/asset_face_recognition/selectors';
import {
  setInConfirmationMode,
  setPersonsStatusFilters,
  toggleShowBoundingBoxes,
} from '../../ducks/panels/asset_face_recognition/slice';
import {
  getPersonsByAssetVersion,
  getPersonsSegments,
} from '../../ducks/panels/asset_face_recognition/thunks';
import { selectCurrentVersionId } from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import ButtonToggle from '../../ui/button_toggle/button_toggle';
import CheckboxFieldWrapper from '../../ui/forms/field_wrappers/checkbox/checkbox';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { EmptyFaceRecognitionPanel } from './empty_face_recognition_panel';
import RecognizedPersons from './recognized_persons/recognized_persons';
import {
  selectFilteredPersons,
  selectPersonCountByStatus,
  selectSegmentsGroupedByPersonId,
  selectShouldShowConfirmModalForVersion,
  selectSortedPersons,
} from './selectors';

import styles from './asset_face_recognition.module.css';

function AssetFaceRecognitionPanel() {
  const personsListState = useAppSelector(
    (state) => state.panelsState.assetPersons,
  );
  const isPersonsLoading = personsListState.loading;

  const inConfirmationMode = useAppSelector(getInConfirmationMode);
  const personsStatusFilters = useAppSelector(
    (state) => state.panelsState.assetFaceRecognition.personsStatusFilters,
  );
  const hasShowBoundingBoxesToggledOn = useAppSelector(
    (state) =>
      state.panelsState.assetFaceRecognition.hasShowBoundingBoxesToggledOn,
  );

  const dispatch = useAppDispatch();
  const { id: assetId, versionId } = useParams({ strict: false });
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, versionId),
  );
  const detectedPersons = useAppSelector((state) =>
    selectSortedPersons(state, currentVersionId),
  );

  const filteredPersons = useAppSelector((state) =>
    selectFilteredPersons(state, personsStatusFilters, currentVersionId),
  );
  const detectedPersonsCountByStatus = useAppSelector((state) =>
    selectPersonCountByStatus(state, currentVersionId),
  );
  const personIdSegmentsMap = useAppSelector((state) =>
    selectSegmentsGroupedByPersonId(state, currentVersionId),
  );

  const isFullscreen = useFullscreen();
  const shouldShowConfirmModal = useAppSelector((state) =>
    selectShouldShowConfirmModalForVersion(state, currentVersionId),
  );

  // TODO(FE-7118): Workaround to stop the face-confirmation modal from stacking
  // on top of the AI metadata modals. Both portal to <body>, so their z-index
  // competes directly and the modal paints on top. The z-index design tokens
  // (FE-6955) do not fix this — a flat scale can't express "above the surface I
  // was opened from, below one opened later"; that needs an open-order stacking
  // system. Revert this once that lands.
  // https://linear.app/iconik/issue/FE-7118
  const isAiMetadataReviewActive = useAppSelector(
    (state) =>
      Boolean(state.modalsManager[AI_METADATA_REVIEW_MODAL]?.isOpen) ||
      findAiMetadataGenerationForAsset(
        state,
        assetId,
        (entry) => entry.pendingModalOpen === true,
      ) !== undefined,
  );
  const isFaceConfirmationModalOpen = useAppSelector((state) =>
    Boolean(state.modalsManager[FACE_CONFIRMATION_MODAL]?.isOpen),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: We need to explicitly exit confirmation mode when asset changes
  useEffect(() => {
    return () => {
      dispatch(setInConfirmationMode(false));
    };
  }, [assetId]);

  useEffect(() => {
    if (!isFullscreen) {
      dispatch(setInConfirmationMode(false));
    }

    if (!currentVersionId || !assetId) {
      return;
    }

    dispatch(
      getPersonsByAssetVersion({
        assetId,
        versionId: currentVersionId,
      }),
    );

    dispatch(getPersonsSegments(assetId, { version_id: currentVersionId }));
  }, [assetId, currentVersionId, isFullscreen]);

  useEffect(() => {
    if (isAiMetadataReviewActive) {
      return;
    }

    if (shouldShowConfirmModal && !isFullscreen) {
      dispatch(
        openModal({
          id: FACE_CONFIRMATION_MODAL,
          contextItems: [],
        }),
      );
    }
  }, [shouldShowConfirmModal, isFullscreen, isAiMetadataReviewActive]);

  useEffect(() => {
    if (isAiMetadataReviewActive && isFaceConfirmationModalOpen) {
      dispatch(closeModal({ id: FACE_CONFIRMATION_MODAL }));
    }
  }, [isAiMetadataReviewActive, isFaceConfirmationModalOpen]);

  if (isPersonsLoading) {
    return <LoadingBar />;
  }

  const hasDetectedPersons = detectedPersons.length > 0;
  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <div className={styles.show_boxes_container}>
          <div>{detectedPersons.length} persons</div>
          <CheckboxFieldWrapper
            label="Show boxes"
            disabled={inConfirmationMode || !hasDetectedPersons}
            value={hasShowBoundingBoxesToggledOn && hasDetectedPersons}
            onChange={(value) => dispatch(toggleShowBoundingBoxes(value))}
          />
        </div>

        <div className={styles.filter_container}>
          <ButtonToggle
            onChange={() =>
              dispatch(
                setPersonsStatusFilters({
                  ...personsStatusFilters,
                  unconfirmed: !personsStatusFilters.unconfirmed,
                }),
              )
            }
            isToggled={
              personsStatusFilters.unconfirmed &&
              detectedPersonsCountByStatus.unconfirmed > 0
            }
            disabled={detectedPersonsCountByStatus.unconfirmed === 0}
            variant="warning"
            className={classNames(styles.filter_item, {
              [styles.filter_item_disabled]:
                detectedPersonsCountByStatus.unconfirmed === 0,
            })}
            size="s"
          >
            {detectedPersonsCountByStatus.unconfirmed} Unconfirmed
          </ButtonToggle>
          <ButtonToggle
            onChange={() =>
              dispatch(
                setPersonsStatusFilters({
                  ...personsStatusFilters,
                  new: !personsStatusFilters.new,
                }),
              )
            }
            isToggled={
              personsStatusFilters.new && detectedPersonsCountByStatus.new > 0
            }
            disabled={detectedPersonsCountByStatus.new === 0}
            variant="white"
            className={classNames(styles.filter_item, {
              [styles.filter_item_disabled]:
                detectedPersonsCountByStatus.new === 0,
            })}
            size="s"
          >
            {detectedPersonsCountByStatus.new} New
          </ButtonToggle>
          <ButtonToggle
            onChange={() =>
              dispatch(
                setPersonsStatusFilters({
                  ...personsStatusFilters,
                  confirmed: !personsStatusFilters.confirmed,
                }),
              )
            }
            isToggled={
              personsStatusFilters.confirmed &&
              detectedPersonsCountByStatus.confirmed > 0
            }
            disabled={detectedPersonsCountByStatus.confirmed === 0}
            className={classNames(styles.filter_item, {
              [styles.filter_item_disabled]:
                detectedPersonsCountByStatus.confirmed === 0,
            })}
            size="s"
          >
            {detectedPersonsCountByStatus.confirmed} Confirmed
          </ButtonToggle>
        </div>
      </div>

      {filteredPersons.length > 0 ? (
        <div className={styles.list_container}>
          <RecognizedPersons
            personIdSegmentsMap={personIdSegmentsMap}
            persons={filteredPersons}
          />
        </div>
      ) : (
        <EmptyFaceRecognitionPanel />
      )}
    </div>
  );
}

export default AssetFaceRecognitionPanel;
